import CerFormPage from '@/pages/superadmin-cer-form';
import CerPage from '@/pages/cer';
import CerLayout from '@/pages/cer-layout';
import CerLegacyRedirect from '@/pages/cer-legacy-redirect';
import { RequireFeature } from '@/components/require-feature';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { ProtectedRoute } from '@/components/protected-route';
import {
	RequireRole,
	RequireSuperadmin,
} from '@/components/role-guard';
import { AppShell } from '@/routes/app-shell';
import { RootLayout } from '@/routes/root-layout';
import { RouteError } from '@/routes/route-error';
import AdminLayout from '@/pages/admin-layout';

import SignInPage from '@/pages/sign-in-page';
import UpdatePasswordPage from '@/pages/update-password';
import DashboardPage from '@/pages/dashboard';
import AIForecastPage from '@/pages/ai-forecast';
import MonitoringPage from '@/pages/monitoring';
import MySlimsPage from '@/pages/myslims';
import NotifichePage from '@/pages/notifiche';
import PersonalizzazioneRedirect from '@/pages/personalizzazione';
import ProfiloPage from '@/pages/profilo';
import AnomaliesPage from '@/pages/anomalies';
import IncentivesPage from '@/pages/incentives';
import ReportsPage from '@/pages/reports';
import DatiPage from '@/pages/dati';
import ConfigurationsPage from '@/pages/configurations';
import ConfigurationFormPage from '@/pages/configuration-form';
import ConfigurationDetailsPage from '@/pages/configuration-details';
import MembersLayout from '@/pages/members-layout';
import MemberDetailsPage from '@/pages/member-details';
import OnbMembersPage from '@/pages/onboarding-members';
import OnbNotificationsPage from '@/pages/onboarding-notifications';
import CerDetailsPage from '@/pages/superadmin-cer-details';
import SuperUsersPage from '@/pages/superadmin-users';
import SuperUserDetailPage from '@/pages/superadmin-user-detail';

// `handle.title` alimenta il titolo del tab del browser (INB-103), risolto in
// `useDocumentTitle`: vince la rotta abbinata più profonda, quindi un padre può
// dare il titolo di sezione e la figlia sovrascriverlo. Le etichette rispecchiano
// la navigazione visibile (sidebar, tab bar), senza emoji.
const router = createBrowserRouter([
	{
		element: <RootLayout />,
		// Boundary unico per tutte le rotte: un errore di render mostra RouteError
		// invece della pagina di default di React Router (INB-192).
		errorElement: <RouteError />,
		children: [
			{ path: '/sign-in', element: <SignInPage />, handle: { title: 'Accedi' } },
			{
				path: '/update-password',
				element: <UpdatePasswordPage />,
				handle: { title: 'Imposta password' },
			},
			{
				// Redirect degli inviti Supabase (`inviteUserByEmail`, apps/api/src/routes/members.ts):
				// stessa meccanica del link di reset password, il socio invitato deve
				// poter scegliere la password invece di finire nel catch-all → /dashboard.
				path: '/auth/callback',
				element: <UpdatePasswordPage />,
				handle: { title: 'Imposta password' },
			},
			{
				element: <ProtectedRoute />,
				children: [
						{
							element: <AppShell />,
							children: [
								{ index: true, element: <Navigate to="/dashboard" replace /> },
								// Shared (all authenticated roles)
								{ path: 'dashboard', element: <DashboardPage />, handle: { title: 'Dashboard' } },
								{
									path: 'monitoring',
									element: <MonitoringPage />,
									handle: { title: 'Monitoraggio' },
								},
								{
									path: 'forecast',
									element: <AIForecastPage />,
									handle: { title: 'Previsione' },
								},
								// Monitoraggio e Previsione erano tab dentro /dashboard; nel mock v11
								// salgono a voci di primo livello. I vecchi indirizzi restano validi:
								// stanno nei preferiti e nelle notifiche già spedite.
								{
									path: 'dashboard/consumption',
									element: <Navigate to="/monitoring" replace />,
								},
								{
									path: 'dashboard/ai-forecast',
									element: <Navigate to="/forecast" replace />,
								},
								{ path: 'myslims', element: <MySlimsPage />, handle: { title: 'MySlims' } },
								{ path: 'notifiche', element: <NotifichePage />, handle: { title: 'Notifiche' } },
								{ path: 'profilo', element: <ProfiloPage />, handle: { title: 'Profilo' } },
								// Le richieste dei soci sono state tolte; le notifiche già spedite le linkano ancora.
								{ path: 'richieste', element: <Navigate to="/dashboard" replace /> },

								// Admin (role >= admin)
								{
									element: <RequireRole min="admin" />,
									children: [
										{
											path: 'incentives',
											element: <IncentivesPage />,
											handle: { title: 'Energia condivisa' },
										},
										{
											element: <RequireFeature feature="reports" />,
											children: [
												{
													path: 'reports',
													element: <ReportsPage />,
													handle: { title: 'Report' },
												},
											],
										},
										{
											element: <RequireFeature feature="anomalies" />,
											children: [{ path: 'anomalies', element: <AnomaliesPage /> }],
											handle: { title: 'Irregolarità Consumi' },
										},
										// `/personalizzazione` è confluita nella tab «Personalizzazione» del
										// dettaglio CER (`/cer/orgs/:id/branding`); qui resta solo il redirect
										// per i vecchi link (notifiche, preferiti).
										{ path: 'personalizzazione', element: <PersonalizzazioneRedirect /> },
										{
											path: 'notifications',
											element: <OnbNotificationsPage />,
											handle: { title: 'Centro Notifiche' },
										},
										{
											path: 'members',
											element: <MembersLayout />,
											handle: { title: 'Soci' },
											children: [
												{ index: true, element: <OnbMembersPage /> },
												{
													path: ':id/details',
													element: <MemberDetailsPage />,
													handle: { title: 'Dettaglio socio' },
												},
											],
										},
										{
											path: 'configurations',
											handle: { title: 'Configurazioni' },
											children: [
												{
													element: <AdminLayout />,
													children: [
														{ index: true, element: <ConfigurationsPage /> },
														{
															path: 'new',
															element: <ConfigurationFormPage />,
															handle: { title: 'Nuova configurazione' },
														},
														{
															path: ':id',
															element: <ConfigurationFormPage />,
															handle: { title: 'Modifica configurazione' },
														},
														{
															path: ':id/details',
															element: <ConfigurationDetailsPage />,
															handle: { title: 'Dettaglio configurazione' },
														},
													],
												},
												// L'arruolamento (fasi, documenti, modifiche) è stato tolto: i vecchi
												// link portano al registro soci invece di aprire `:id` = «onboarding».
												{ path: 'onboarding/*', element: <Navigate to="/members" replace /> },
											],
										},
										{
											path: 'cer',
											element: <AdminLayout />,
											handle: { title: 'Gestione CER' },
											children: [
												{ index: true, element: <CerPage /> },
												{
													path: 'orgs/:id/details',
													element: <CerLayout />,
													handle: { title: 'Dettaglio CER' },
													children: [
														{ index: true, element: <CerDetailsPage /> },
														{ path: 'branding', element: <CerLegacyRedirect section="branding" /> },
														{ path: 'dati', element: <CerLegacyRedirect section="dati" /> },
														{
															element: <RequireSuperadmin />,
															children: [
																{ path: 'general', element: <CerLegacyRedirect section="general" /> },
																{ path: 'features', element: <CerLegacyRedirect section="features" /> },
																{ path: 'users', element: <CerLegacyRedirect section="users" /> },
																{ path: 'users/:userId', element: <CerLegacyRedirect section="users" /> },
															],
														},
													],
												},
												{
													element: <RequireSuperadmin />,
													children: [
														{ path: 'orgs/new', element: <CerFormPage />, handle: { title: 'Nuova CER' } },
														{ path: 'users', element: <CerLegacyRedirect section="users" /> },
														{ path: 'users/:userId', element: <CerLegacyRedirect section="users" /> },
													],
												},
												{ path: 'dati', element: <CerLegacyRedirect section="dati" /> },
												{ path: 'orgs/:id', element: <CerFormPage />, handle: { title: 'Modifica CER' } },
												{ path: 'orgs/:id/branding', element: <CerLegacyRedirect section="branding" /> },
												{ path: 'orgs/:id/features', element: <CerLegacyRedirect section="features" /> },
											],
										},
									],
								},
								// Platform tools are separate from each community detail.
								{
									element: <RequireSuperadmin />,
									children: [{ element: <AdminLayout />, children: [
										{ path: 'dati', element: <DatiPage />, handle: { title: 'Dati' } },
										{ path: 'users', element: <SuperUsersPage />, handle: { title: 'Utenti' } },
										{ path: 'users/:userId', element: <SuperUserDetailPage />, handle: { title: 'Dettaglio utente' } },
									] }],
								},
							],
						},
				],
			},
			{ path: '*', element: <Navigate to="/dashboard" replace /> },
		],
	},
]);

export function App() {
	return <RouterProvider router={router} />;
}
